import { NO_POSITION_TYPES, dateAxis } from './period';

export const METRICS = ['clicks', 'impressions', 'ctr', 'position'];

/** Whether the surface being reported on has a ranking position at all. */
export function reportsPosition(searchType) {
    return !NO_POSITION_TYPES.has(searchType);
}

/**
 * The metrics one search type actually reports.
 *
 * Discover and Google News have no ranking position, and Search Console sends a 0 in that field
 * rather than omitting it — a 0 that would read as a perfect rank. The metric is dropped for
 * those surfaces instead: no card, no series, no column.
 */
export function metricsFor(searchType) {
    return reportsPosition(searchType) ? METRICS : METRICS.filter((name) => name !== 'position');
}

/** Metrics where a smaller number is the better one. */
export const LOWER_IS_BETTER = { position: true };

/**
 * Turns one Search Console row into the metrics a report shows, or null when there is no row.
 *
 * A missing row is not a zero: an empty successful response means the property returned nothing
 * for that scope, which is reported as unavailable rather than as four zeroes. Zero impressions
 * is a real answer, but it leaves CTR and average position undefined — Search Console returns 0
 * for both — so they are nulled with a reason instead of being drawn as a 0% CTR and a position
 * of 0, which would read as a perfect rank. A surface that reports no position at all is nulled
 * for the same reason.
 */
export function normalizeTotals(rows, hasPosition = true) {
    const row = rows?.[0];
    if (!row) return null;

    const impressions = row.impressions ?? 0;
    const hasImpressions = impressions > 0;

    return {
        clicks: row.clicks ?? 0,
        impressions,
        ctr: hasImpressions ? row.ctr ?? 0 : null,
        position: hasImpressions && hasPosition ? row.position ?? 0 : null,
        reason: hasImpressions ? null : 'no_impressions',
    };
}

/**
 * The metrics of one returned row, keyed by its first dimension value.
 *
 * Used wherever rows are read by key rather than aggregated: page and query tables, contributor
 * lists. CTR and position are nulled on a row with no impressions for the same reason as above.
 */
export function rowMetrics(row, hasPosition = true) {
    const impressions = row?.impressions ?? 0;
    return {
        clicks: row?.clicks ?? 0,
        impressions,
        ctr: impressions > 0 ? row?.ctr ?? 0 : null,
        position: impressions > 0 && hasPosition ? row?.position ?? 0 : null,
    };
}

/**
 * Totals recomputed from a set of returned rows.
 *
 * CTR comes from the summed clicks over the summed impressions and never from averaging the rows'
 * own CTRs, which would weight a row with ten impressions like one with ten thousand. Average
 * position is impression-weighted for the same reason, and is only offered where the surface
 * reports one.
 *
 * These are the totals *of the rows retrieved*, which is not the property's total: Search Console
 * withholds rows below its own thresholds, so a caller must label them as such rather than
 * substituting them for an aggregate request.
 */
export function sumRows(rows, hasPosition = true) {
    let clicks = 0;
    let impressions = 0;
    let weightedPosition = 0;

    for (const row of rows ?? []) {
        clicks += row?.clicks ?? 0;
        impressions += row?.impressions ?? 0;
        if (hasPosition && row?.position !== null && row?.position !== undefined) {
            weightedPosition += row.position * (row?.impressions ?? 0);
        }
    }

    return {
        clicks,
        impressions,
        ctr: impressions > 0 ? clicks / impressions : null,
        position: hasPosition && impressions > 0 ? weightedPosition / impressions : null,
    };
}

/**
 * Which of the dates a period asked for actually came back.
 *
 * Search Console omits days it has nothing for rather than returning them as zero, so a missing
 * day is unknown, not quiet. The distinction drives both the gaps in a trend and whether the
 * period is allowed to take part in a comparison at all.
 */
export function coverageFor(rows, period) {
    const requested = dateAxis(period);
    const observed = new Set((rows ?? []).map((row) => row?.keys?.[0]).filter(Boolean));
    const missing = requested.filter((date) => !observed.has(date));

    return {
        requested,
        observed: [...observed].sort(),
        missing,
        complete: requested.length > 0 && missing.length === 0,
    };
}

/**
 * The daily rows laid out against the period's full calendar axis.
 *
 * Every requested date gets an entry; days the response omitted carry null metrics so a chart can
 * leave a gap there. Nothing is interpolated — a line drawn through a missing day would be
 * inventing an observation.
 */
export function buildSeries(rows, period, hasPosition = true) {
    const byDate = {};
    for (const row of rows ?? []) {
        const date = row?.keys?.[0];
        if (date) byDate[date] = row;
    }

    return dateAxis(period).map((date) => {
        const row = byDate[date];
        if (!row)
            return {
                date,
                returned: false,
                clicks: null,
                impressions: null,
                ctr: null,
                position: null,
            };

        return { date, returned: true, ...rowMetrics(row, hasPosition) };
    });
}

/**
 * The change from baseline to current for one metric.
 *
 * Clicks and impressions get an absolute change and a percentage, the latter only where the
 * baseline is above zero — growth from nothing has no percentage, however tempting an infinity
 * looks. CTR is compared in percentage points, because the difference between two ratios is not
 * itself a ratio, and average position in points with the sign kept as-is: a negative change
 * means the property moved up the results.
 *
 * Everything is computed from the raw API values and rounded only when displayed.
 */
export function metricDelta(metricName, current, baseline) {
    if (current === null || current === undefined || baseline === null || baseline === undefined) {
        return { absolute: null, percent: null };
    }

    const absolute = current - baseline;

    if (metricName === 'ctr') {
        // ctr arrives as a fraction; the displayed unit is percentage points.
        return { absolute: absolute * 100, percent: null, unit: 'pp' };
    }

    if (metricName === 'position') {
        return { absolute, percent: null, unit: 'points' };
    }

    return { absolute, percent: baseline > 0 ? (100 * absolute) / baseline : null };
}

/**
 * Whether two periods may be compared at all.
 *
 * Both have to have returned every date they asked for. A period with unresolved days has an
 * unknown total, and a comparison against an unknown total is a guess — so the deltas are
 * withheld with a reason rather than being shown against an incomplete baseline.
 *
 * Preliminary data cannot be detected here: the proxy returns the response rows only, so Search
 * Console's `metadata.firstIncompleteDate` never reaches the browser. Requesting final data (the
 * default) is what keeps still-collecting days out of the figures.
 */
export function comparability(currentCoverage, baselineCoverage) {
    if (!baselineCoverage) return { comparable: false, reason: 'no_baseline' };
    if (!currentCoverage?.complete || !baselineCoverage.complete) {
        return { comparable: false, reason: 'incomplete_coverage' };
    }
    return { comparable: true, reason: null };
}

/**
 * Joins two periods' rows on the exact key Search Console returned.
 *
 * The join is a full outer one: a key present in only one period is kept, with the other side
 * marked as not returned. That is deliberately not treated as zero, new or lost — Search Console
 * omits rows below its own thresholds, so absence here is an absence of information, and only a
 * row with both sides can take part in a gain or a decline.
 *
 * Keys are compared verbatim. Lower-casing URLs, stripping query strings or normalising trailing
 * slashes would merge rows Search Console reports separately and silently double a row's clicks;
 * the same goes for trimming query text.
 */
export function joinRows(currentRows, priorRows, hasPosition = true) {
    const read = (rows) => {
        const byKey = new Map();
        for (const row of rows ?? []) {
            const key = row?.keys?.[0];
            if (!key) continue;
            byKey.set(key, rowMetrics(row, hasPosition));
        }
        return byKey;
    };

    const current = read(currentRows);
    const prior = read(priorRows);

    const joined = [];
    for (const key of new Set([...current.keys(), ...prior.keys()])) {
        const currentMetrics = current.get(key) ?? null;
        const priorMetrics = prior.get(key) ?? null;
        const comparable = !!currentMetrics && !!priorMetrics;

        joined.push({
            id: key,
            key,
            // The page table reads this as a URL and the query table as query text; both are the
            // exact string Search Console returned.
            url: key,
            current: currentMetrics,
            prior: priorMetrics,
            comparable,
            deltaClicks: comparable ? currentMetrics.clicks - priorMetrics.clicks : null,
            deltaPercent:
                comparable && priorMetrics.clicks > 0
                    ? (100 * (currentMetrics.clicks - priorMetrics.clicks)) / priorMetrics.clicks
                    : null,
        });
    }

    return sortByMovement(joined);
}

/**
 * Biggest click movement first, in either direction, with the rows that cannot be compared last
 * — they have no movement to rank, and burying them keeps them out of the gains and declines they
 * are not part of. Ties fall back to the key so the order is stable between loads.
 */
export function sortByMovement(rows) {
    return [...rows].sort((a, b) => {
        if (a.comparable !== b.comparable) return a.comparable ? -1 : 1;
        const movement = Math.abs(b.deltaClicks ?? 0) - Math.abs(a.deltaClicks ?? 0);
        if (movement !== 0) return movement;
        return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
    });
}

/** Duplicate keys within one period mean the chunks cannot be trusted to be counted once. */
export function hasDuplicateKeys(rows) {
    const keys = (rows ?? []).map((row) => row?.keys?.[0]).filter(Boolean);
    return keys.length !== new Set(keys).size;
}
